/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {InnerVerticalTabs} from "../../../../components/Tabs/InnerVerticalTabs/InnerVerticalTabs";
import Content from "./Content";
import {BooleanParam, StringParam, useQueryParam, withDefault} from "use-query-params";
import {ArgosFavoriteIconLogic} from "../../../../components/Buttons/ArgosFavoriteIconLogic";
import {FAVORITE_PARAM_ID, FAVORITE_TYPE, INNERVTAB_URL_PARAM} from "../constants";
import {Lidar} from "../../constants";
import {ObjectDef} from "../../../objectApp/constants";
import {
  findObjectByVariantId
} from "../../../radar/SensorRadar/ItemDetails/RadarPerformance/CollapsibleTabs/ObjectProperties";
import {MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME} from "../../../radar/SensorRadar/ItemDetails/constants";


export const ViewPublicConfigsParam = withDefault(BooleanParam, false)

interface LidarPerformanceProps {
  sensor: Lidar
  // objects: ObjectDef[]
  objects: any[]
  gmid: string
  onSaveConfiguration: any
  onDeleteConfiguration: any
}

const LidarPerformance = (props: LidarPerformanceProps) => {

  const [tabs, setTabs] = useState<any>([])
  const [viewPublicConfigurations, setViewPublicConfigurations] = useQueryParam('viewPublicConfigs', ViewPublicConfigsParam)
  const [_index, fff] = useQueryParam(FAVORITE_PARAM_ID, StringParam)

  function getBaselineObject(tempToObjMap: any) {
    const tempValue = Object.keys(tempToObjMap)[0]; // Get the first temperature value as default
    const _newObjectName = tempToObjMap?.[tempValue]?.["objectName"];
    const pointsFrames = tempToObjMap?.[tempValue]?.["pointsFrames"];
    const version = tempToObjMap?.[tempValue]?.["objectVersion"];
    const newObj = {
      "objectCategory": tempToObjMap?.[tempValue]?.["objectCategory"],
      "objectName": _newObjectName,
      "pointsFrames": pointsFrames,
      "objectVersion": version,
      objectVariant: tempToObjMap?.[tempValue]?.["objectVariant"]
    };
    return newObj;
  }


  const getTemperatureToObjectMapping = () => {
    const sensorData: any = props.sensor?.plotData;
    const baselineData = props.sensor?.baseline;
    const tempToObjMap1: any = {};
    const tempToObjMap2: any = {};

    Object.keys(sensorData)?.forEach((detectionTable: any) => {
      if (Object.keys(sensorData[detectionTable]).length !== 0) {
        const tableData = sensorData?.[detectionTable]?.data;
        const tempKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("temperature") && key.includes("value"))[0];
        const pointsFramesKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("points_frames") && key.includes("value"))[0];

        const tempValueAsString: string = String(tableData[tempKey]);

        if (detectionTable === "Detection_Range_1" || detectionTable === "Detection_Range_2") {
          tempToObjMap1[tempValueAsString] = {
            "pointsFrames": tableData[pointsFramesKey],
            "objectName": baselineData?.[0]?.objectName,
            "objectCategory": baselineData?.[0]?.objectCategory,
            "objectVariant": baselineData?.[0]?.objectVariant,
            objectVersion: baselineData?.[0]?.objectVersion
          };
        } else if (detectionTable === "Detection_Range_3" || detectionTable === "Detection_Range_4") {
          tempToObjMap2[tempValueAsString] = {
            "pointsFrames": tableData[pointsFramesKey],
            "objectName": baselineData?.[1]?.objectName,
            "objectCategory": baselineData?.[1]?.objectCategory,
            "objectVariant": baselineData?.[1]?.objectVariant,
            objectVersion: baselineData?.[1]?.objectVersion
          };
        }
      }
    });
    return {tempToObjMap1, tempToObjMap2};
  };

  const groupBy = (array: any, key: any) =>
    array?.reduce((objectsByKeyValue: any, obj: any) => {
      const value = obj[key];
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});

  useEffect(() => {
    const objectData = props.objects;
    const sensorLidarData = props.sensor;
    const sensorLidarConfigs = sensorLidarData.configurations;
    const baselineFromDatabase = sensorLidarData.baseline[0]

    const {tempToObjMap1, tempToObjMap2} = getTemperatureToObjectMapping();

    const baselineObject1 = getBaselineObject(tempToObjMap1)
    const baselineObject2 = getBaselineObject(tempToObjMap2);

    sensorLidarData["pointsFrames"] = baselineObject1.pointsFrames;

    const tempLocalObjectDataMap = Object.entries(groupBy(objectData, 'id')).map<any>(([key, value]: [any, any]) => {
      return {
        'type': value[0].type,
        'name': value[0].name,
        'sub': value.map((elem: any) => ({...elem}))
      }
    })
    /**
     * Filters and maps object data to include only allowed types with relevant sub-data.
     *
     * @constant
     * @type {Array<Object>}
     * @description
     * - Filters `tempLocalObjectDataMap` to include only objects with types in `allowedTypes`.
     * - Groups data by `type` and maps it to an array of objects with `type` and `sub` properties.
     *
     * @example
     * // Result format:
     * [
     *   { type: "Baseline", sub: [...] },
     *   { type: "Sign", sub: [...] }
     * ]
     */
    const allowedTypes = ["Baseline", "Potentially Moving Object", "Sign", "Custom", 'Miscellaneous']
    const localObjectDataMap = Object.entries(groupBy(tempLocalObjectDataMap, 'type'))
      .filter(([key]) => allowedTypes.includes(key))
      .map(([key, value]: any) => ({
        type: key,
        sub: value.map((elem: any) => elem.sub),
      }))

    // this is baselineObject constructed from database
    const baselineObject = {
      "type": "Baseline",

      "name": baselineFromDatabase.objectName,
      "objectVariant": baselineFromDatabase.objectVariant,
      "version": baselineFromDatabase.objectVersion,

      "widthHorizontal": baselineFromDatabase.orientationWidth,
      "heightVertical": baselineFromDatabase.orientationHeight,

      "rcs": baselineFromDatabase.rcs,

      "pixelsAcrossShortDimension": "",
      "pointsOnObject": "",
    }

    localObjectDataMap.push({'type': 'Custom', 'sub': [{}]})
    localObjectDataMap.unshift({'type': 'Baseline', 'sub': [[baselineObject]]})
    objectData.push(baselineObject)

    const defaultTemperatureValue = Object.keys(tempToObjMap1)[0]

    const baselineData = {
      "sensorId": sensorLidarData.id,

      "objectCategory": baselineObject.type,
      "objectName": baselineObject.name,
      "objectVersion": baselineObject.version,
      "objectVariant": baselineObject.objectVariant,

      "orientationWidth": baselineObject.widthHorizontal,
      "orientationHeight": baselineObject.heightVertical,

      'temperature': defaultTemperatureValue,

      'azimuthFOV': sensorLidarData.azimuthFOV,
      'elevationFOV': sensorLidarData.elevationFOV,
      'maxInstrumentedRange': sensorLidarData.maxInstrumentedRange,

      pointsOnTargetWidth: 5,
      pointsOnTargetHeight: 5,

      'isPublicConfig': true
    }

    let localTabs: any[] = []

    // Baseline configuration
    const baselineTab = {
      "trackedItemUid": 'baseline',
      "name": "Baseline",
      "content": <Content configIndex={0} baselineData={baselineData} objectData={objectData}
                          objectDataMap={localObjectDataMap}
                          sensorLidarData={sensorLidarData} onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    }
    localTabs.push(baselineTab)

    // Owner configurations
    const ownerConfigTabs = sensorLidarConfigs?.filter((x: object) => x["owner" as keyof object] == props.gmid)
    const configTabs = ownerConfigTabs?.map((singleConfig: any, index: number) => {
        updateSingleConfig(singleConfig, objectData)
        return (
          {
            "trackedItemUid": singleConfig['trackedItemUid'] ?? `item-${index}-${singleConfig['configName']}`,
            "name": singleConfig['configName'],
            "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                                objectDataMap={localObjectDataMap} sensorLidarData={sensorLidarData}
                                onSaveConfiguration={props.onSaveConfiguration}
                                onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
          }
        )
      }
    )
    if (configTabs != null) {
      localTabs = localTabs.concat(configTabs)
    }

    // Public configurations
    if (viewPublicConfigurations) {
      const publicConfigs = sensorLidarConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid);
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => ({
        "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
        "name": singleConfig['configName'],
        "content": <Content configIndex={index + 2} baselineData={singleConfig} objectData={objectData}
                            tempToObjMap={tempToObjMap1}
                            sensorLidarData={sensorLidarData}
                            onSaveConfiguration={props.onSaveConfiguration}
                            onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
      }));
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs);
      }
    }

    if (viewPublicConfigurations) {
      const publicConfigs = sensorLidarConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid)
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => {
        updateSingleConfig(singleConfig, objectData)
        return (
          {
            "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
            "name": singleConfig['configName'],
            "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                                objectDataMap={localObjectDataMap} sensorLidarData={sensorLidarData}
                                onSaveConfiguration={props.onSaveConfiguration}
                                onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
          }
        )
      })
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs)
      }
    }

    setTabs(localTabs);
  }, [props.sensor, props.objects, viewPublicConfigurations, props.gmid, props.onDeleteConfiguration, props.onSaveConfiguration]);


  return (
    <div style={{flexGrow: 1, display: 'flex', height: '100%', flexDirection: "column"}}>
      <div style={{display: 'flex', width: '100%'}}>
        <div style={{marginLeft: 'auto'}}>
          <ArgosFavoriteIconLogic favoriteType={FAVORITE_TYPE} trackedItemUid={props.sensor?.id} partitionKey={props.sensor?.id}/>
        </div>
      </div>
      <InnerVerticalTabs tabs={tabs} favoriteType={FAVORITE_TYPE} uid={INNERVTAB_URL_PARAM}
        // onTabSelected={(index: number) => setSelectedTab(index)}
      />
    </div>
  )
}
export default LidarPerformance;

const updateSingleConfig = (singleConfig: any, objectData: any) => {
  const isBaseline = !('objectId' in singleConfig)
  if (!isBaseline) {
    const objectId = singleConfig['objectId']
    const variantData = findObjectByVariantId(objectData, objectId)
    const variantObj = variantData?.object

    const objectCategory = variantObj?.type
    const objectName = variantObj?.objectName
    const objectVersion = variantObj?.latestVersion
    const profileNameKey = variantData?.variantName
    const objectVariant = MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME[profileNameKey ?? '']

    singleConfig['objectCategory'] = objectCategory
    singleConfig['objectName'] = objectName
    singleConfig['objectVersion'] = objectVersion
    singleConfig['objectVariant'] = objectVariant

    const rcs = variantObj?.parameters?.[profileNameKey ?? '']?.radarCrossSection ?? ''
    singleConfig['rcs'] = rcs
  }
}